import React, { useRef } from 'react';
import useFocusTrap from '../hooks/useFocusTrap';
import styles from '../styles/Modal.module.css';

/**
 * Modal — the app's single card-dialog shell.
 *
 * Owns everything a dialog must always do the same way:
 *  - backdrop: blurred app behind a light shade (product direction:
 *    "blur the background with modals, not just shade")
 *  - panel: white card, --radius-lg, standard padding + entrance motion
 *  - keyboard: focus trapped inside, Escape closes, focus restored on close
 *    (via the shared useFocusTrap hook)
 *  - a single canonical ✕ close button (44px hit area)
 *
 * NOT for full-screen celebrations (bead unlocks, mission complete) — those
 * are immersive takeovers with their own visual language, not card dialogs.
 *
 * Props:
 *  - isOpen, onClose (required; omit onClose for a non-dismissible dialog)
 *  - children: panel content
 *  - size: 'sm' (360px) | 'md' (480px, default) | 'lg' (640px)
 *  - tone: 'light' (default, white card) | 'dark' (near-black card for
 *    media/showcase dialogs like the bead detail viewer)
 *  - position: 'center' (default) | 'sheet' (bottom sheet on mobile,
 *    centers at ≥560px)
 *  - ariaLabel or labelledBy: accessible name (one of them, please)
 *  - closeOnBackdrop: default true
 *  - showClose: default true — the ✕ in the top-right corner
 */
function Modal({
  isOpen,
  onClose,
  children,
  size = 'md',
  tone = 'light',
  position = 'center',
  ariaLabel,
  labelledBy,
  closeOnBackdrop = true,
  showClose = true
}) {
  const panelRef = useRef(null);
  useFocusTrap(panelRef, isOpen, onClose);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={`${styles.backdrop} ${position === 'sheet' ? styles.backdropSheet : ''}`}
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        className={[
          styles.panel,
          styles[size] || styles.md,
          tone === 'dark' ? styles.dark : '',
          position === 'sheet' ? styles.sheet : ''
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {showClose && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close dialog"
          >
            <span aria-hidden="true">✕</span>
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

export default Modal;
